import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { ArrowRight, ArrowUpRight, Check, FileText, FolderOpen, LoaderCircle, LogOut, ShieldCheck } from 'lucide-react';
import { initializeAnalytics } from './config/firebase';
import { authErrorMessage, logOut, registerWithEmail, resetPassword, signInWithEmail, signInWithGoogle, subscribeToAuth } from './services/authService';
import { startProfileSync } from './services/userProfileService';
import ProfileModal from './components/ProfileModal';
import SuperadminButton from './components/SuperadminButton';
import logo from './assets/images/logo.png';

const CanvasEditor = lazy(() => import('./components/shared/CanvasEditor'));
const ProductionWorkspace = lazy(() => import('./components/shared/ProductionWorkspace'));

function Brand() {
  return <a className="brand" href="/" aria-label="ParrotSuite home"><img src={logo} alt="ParrotSuite" className="h-10 w-auto" /></a>;
}

function AppLoading({ children }) {
  return <div className={children ? 'app-loading flex-col' : 'app-loading'}><Brand /><LoaderCircle className="spin" aria-label="Loading canvas" />{children}</div>;
}

function AuthScreen() {
  const [mode, setMode] = useState('login');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  async function run(action) {
    setMessage(null); setBusy(true);
    try { await action(); }
    catch (error) { setMessage({ error: true, text: authErrorMessage(error) }); }
    finally { setBusy(false); }
  }
  function submit(event) {
    event.preventDefault();
    const values = new FormData(event.currentTarget);
    run(async () => {
      if (mode === 'reset') {
        await resetPassword(values.get('email'));
        setMessage({ text: 'If an account exists, a reset link has been sent. Check your inbox.' });
      } else if (mode === 'register') {
        await registerWithEmail(values.get('name'), values.get('email'), values.get('password'));
      } else await signInWithEmail(values.get('email'), values.get('password'));
    });
  }
  return <div className="auth-page">
    <section className="auth-story"><Brand /><div className="story-content"><span className="eyebrow"><span className="status-dot" /> A LITTLE SPACE FOR BIG IDEAS</span><h1>Everything you’re<br />working on.<br /><em>All together.</em></h1><p>Create, edit, and bring your ideas to life on one canvas.</p>
      <div className="workspace-art" aria-hidden="true"><div className="art-orbit" /><div className="art-card art-note"><span className="art-label">THE NEXT BIG THING</span><FileText size={32} /><strong>It starts with an idea.</strong><div className="art-lines"><i /><i /><i /></div><span className="art-tag">A little more possibility ↗</span></div><div className="art-card art-file"><div className="art-file-icon"><FolderOpen size={32} /></div><div><strong>Your work, connected.</strong><small>Always within reach</small></div><span className="art-check"><Check size={16} /></span></div><span className="art-spark">✳</span></div>
      </div><footer className="story-footer"><span>Less scattered. More inspired.</span><span>MADE FOR YOUR EVERYDAY ↗</span></footer>
    </section>
    <section className="auth-main"><div className="mobile-brand"><Brand /></div><div className="auth-top">{mode === 'register' ? 'Already have an account?' : 'New around here?'} <button disabled={busy} onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setMessage(null); }}>{mode === 'register' ? 'Sign in' : 'Create an account'} <ArrowUpRight size={14} /></button></div>
      <div className="auth-form-wrap"><span className="small-kicker">YOUR CANVAS, READY WHEN YOU ARE</span><h2>{mode === 'register' ? 'Make yourself at home.' : mode === 'reset' ? 'A fresh start.' : 'Good to have you back.'}</h2><p className="muted">{mode === 'register' ? 'Create an account and give your work a place to grow.' : mode === 'reset' ? 'Enter your email and we’ll send a password reset link.' : 'Sign in to pick up where inspiration left off.'}</p>
        {mode !== 'reset' && <><button className="google-button" disabled={busy} onClick={() => run(signInWithGoogle)}><span className="google-g">G</span>Continue with Google</button><div className="divider"><span />or continue with email<span /></div></>}
        <form onSubmit={submit} key={mode}>
          {mode === 'register' && <label>Full name<input name="name" placeholder="Your name" autoComplete="name" maxLength={100} required disabled={busy} /></label>}
          <label>Email address<input name="email" type="email" placeholder="you@example.com" autoComplete="email" required disabled={busy} /></label>
          {mode !== 'reset' && <div className="password-field"><div className="label-row"><label htmlFor="password">Password</label>{mode === 'login' && <button type="button" className="text-button" disabled={busy} onClick={() => { setMode('reset'); setMessage(null); }}>Forgot password?</button>}</div><input id="password" name="password" type="password" placeholder={mode === 'register' ? 'At least 8 characters' : 'Enter your password'} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} minLength={mode === 'register' ? 8 : undefined} required disabled={busy} /></div>}
          {message && <div className={message.error ? 'notice error' : 'notice'} role={message.error ? 'alert' : 'status'}>{message.text}</div>}
          <button className="primary full" disabled={busy}>{busy ? <LoaderCircle size={18} className="spin" /> : <>{mode === 'register' ? 'Create your account' : mode === 'reset' ? 'Send reset link' : 'Sign in to your canvas'}<ArrowRight size={18} /></>}</button>
        </form>
        {mode === 'reset' && <button className="text-button back-button" onClick={() => { setMode('login'); setMessage(null); }}>Back to sign in</button>}
        <div className="auth-assurance"><ShieldCheck size={16} />Your canvas is private. Your ideas are yours.</div>
      </div><footer className="auth-footer"><span>© {new Date().getFullYear()} ParrotSuite</span><span>A little more organized. A lot more you.</span></footer>
    </section>
  </div>;
}

function CanvasApplication({ user }) {
  const canvasRef = useRef(null);
  const [workspacePage, setWorkspacePage] = useState('canvas');
  const [productionVisited, setProductionVisited] = useState(false);
  const profileSyncRef = useRef(null);
  const [profileState, setProfileState] = useState({ syncing: true, error: null });
  const [profileOpen, setProfileOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState(null);
  const [failedPhotoUrl, setFailedPhotoUrl] = useState(null);
  const photoUrl = user.providerData?.find((provider) => provider.providerId === 'google.com')?.photoURL || user.photoURL;
  const productionEnabled = profileState.accountType === 'PRO';
  const showingProduction = productionEnabled && workspacePage === 'production';
  function navigate(page) {
    setWorkspacePage(page);
    if (page === 'production') setProductionVisited(true);
  }
  useEffect(() => {if (showingProduction) setProductionVisited(true);}, [showingProduction]);

  useEffect(() => {
    const sync = startProfileSync(next => setProfileState(current => ({ ...current, ...next })));
    profileSyncRef.current = sync;
    return () => { sync.stop(); profileSyncRef.current = null; };
  }, [user]);

  async function handleSignOut() {
    setSigningOut(true);
    setError(null);
    try { await logOut(); }
    catch (failure) { setError(authErrorMessage(failure)); }
    finally { setSigningOut(false); }
  }

  const accessDenied = ['pending', 'paused', 'removed'].includes(profileState.status);
  if (profileState.status !== 'active' && !accessDenied) return <AppLoading>
    {profileState.error && <div className="max-w-sm px-6 text-center">
      <p className="notice error" role="alert">{profileState.error}</p>
      {error && <p className="notice error" role="alert">{error}</p>}
      <div className="profile-actions"><button className="text-button" disabled={profileState.syncing} onClick={() => profileSyncRef.current?.retry()}>Retry profile sync</button><button className="text-button" disabled={signingOut} onClick={handleSignOut} aria-label="Sign out">Sign out</button></div>
    </div>}
  </AppLoading>;

  if (accessDenied) return <div className="access-page"><Brand /><div className="access-card">
    <ShieldCheck size={32} /><h1>{profileState.status === 'pending' ? 'Awaiting approval' : profileState.status === 'paused' ? 'Your access is paused' : 'Your access has been removed'}</h1>
    <p>{profileState.status === 'pending' ? 'A superadmin needs to approve your account before you can open your canvas.' : profileState.status === 'paused' ? 'A superadmin has paused your ParrotSuite account.' : 'This account no longer has access to ParrotSuite.'}</p>
    {profileState.error && <p role="alert">{profileState.error}</p>}{error && <p role="alert">{error}</p>}
    <div className="profile-actions"><button className="admin-button" disabled={profileState.syncing} onClick={() => profileSyncRef.current?.retry()}>{profileState.error ? 'Retry profile sync' : 'Check access again'}</button><button className="admin-button" disabled={signingOut} onClick={handleSignOut} aria-label="Sign out">Sign out</button></div>
  </div></div>;

  return <div className="canvas-application fixed inset-0 h-screen overflow-hidden">
    <main className="canvas-app-main" aria-label={showingProduction ? 'Production workspace' : 'Canvas editor'}>
      <div className="workspace-page" style={{display: showingProduction ? 'none' : 'flex'}}>
      <Suspense fallback={<div className="canvas-loading" role="status"><LoaderCircle className="spin" size={22} />Opening your canvas…</div>}>
        <CanvasEditor
          ref={canvasRef}
          active={!showingProduction}
          userId={user.uid}
          productionEnabled={productionEnabled}
          onNavigateProduction={() => navigate('production')}
          fullPage
          toolbarLeading={<div className="canvas-menu-brand"><Brand /></div>}
          sidebarFooter={<div className="canvas-profile">
            <div className="canvas-profile-row">
              <button type="button" className="canvas-profile-open" onClick={() => setProfileOpen(true)} aria-label="Open profile">
              <span className="canvas-profile-avatar" aria-hidden="true">
                {photoUrl && photoUrl !== failedPhotoUrl
                  ? <img src={photoUrl} alt="" referrerPolicy="no-referrer" onError={() => setFailedPhotoUrl(photoUrl)} className="h-full w-full rounded-full object-cover" />
                  : (user.displayName || user.email || 'U')[0].toUpperCase()}
              </span>
              <span className="canvas-profile-details"><strong>{user.displayName || 'Your account'}</strong><span title={user.email}>{user.email}</span></span>
              </button>
              {profileState.role === 'superadmin' && <SuperadminButton user={user} />}
              <button onClick={handleSignOut} disabled={signingOut} aria-label="Sign out" title={signingOut ? 'Signing out…' : 'Sign out'}><LogOut size={16} /></button>
            </div>
            {profileState.error && <div className="canvas-profile-error" role="alert">{profileState.error}<button type="button" className="profile-sync-retry" disabled={profileState.syncing} onClick={() => profileSyncRef.current?.retry()}>Retry profile sync</button></div>}
            {error && <p className="canvas-profile-error" role="alert">{error}</p>}
          </div>}
        />
      </Suspense>
      </div>
      {productionEnabled && (productionVisited || showingProduction) && <div className="workspace-page" style={{display: showingProduction ? 'flex' : 'none'}}>
        <Suspense fallback={<div className="canvas-loading" role="status">Opening Production…</div>}><ProductionWorkspace userId={user.uid} toolbarLeading={<div className="canvas-menu-brand"><Brand /></div>} onBackCanvas={() => navigate('canvas')} onOpenFile={file => {navigate('canvas'); canvasRef.current?.openFile(file);}} /></Suspense>
      </div>}
    </main>
    {profileOpen && <ProfileModal user={user} onClose={() => setProfileOpen(false)} />}
  </div>;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    initializeAnalytics();
    return subscribeToAuth((nextUser) => { setUser(nextUser); setReady(true); });
  }, []);
  if (!ready) return <AppLoading />;
  return user ? <CanvasApplication user={user} key={user.uid} /> : <AuthScreen />;
}
